import React from "react";
import FallBackUi from "../../plugin/FallBackUi";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@components/ui/tabs";
import { toast } from "@components/ui/use-toast";
import { useKeySearch } from "./useKeySearch";
import { KeySearchBadge } from "./KeySearchBadge";

interface SourceData {
  label: string;
  data: any;
  isConfig?: boolean;
}

interface InputViewerProps {
  data?: any;
  draggable?: boolean;
  onDragStart?: (path: string, value: any) => void;
  label?: string;
  showLabel?: boolean;
  className?: string;
  dragDataFormat?: "path" | "expression" | "config";
  showHeader?: boolean;
  sourceCount?: number;
  excludeKeys?: string[];
  onRefresh?: () => void;
  sources?: SourceData[];
}

export const InputViewer: React.FC<InputViewerProps> = ({
  data,
  draggable = false,
  onDragStart,
  label,
  showLabel = true,
  className = "",
  dragDataFormat = "expression",
  showHeader = false,
  sourceCount = 0,
  onRefresh,
  sources,
}) => {
  const [activeTab, setActiveTab] = React.useState("0");
  const excludeKeys: string[] = [];

  const panelContainerRefs = React.useRef<
    Record<string, HTMLDivElement | null>
  >({});

  const normalizedSources = React.useMemo(() => {
    if (sources && sources.length > 0) return sources;
    if (data && label) return [{ label, data }];
    if (data) return [{ label: "Data", data }];
    return [];
  }, [sources, data, label]);

  const useTabs = normalizedSources.length > 1;

  const activeSourceData = useTabs
    ? normalizedSources[Number(activeTab)]?.data
    : normalizedSources[0]?.data;

  // ── Shared key-search logic (see useKeySearch.ts) ───────────────────────────
  const keySearch = useKeySearch({
    data: activeSourceData,
    excludeKeys,
    getContainer: () =>
      panelContainerRefs.current[useTabs ? activeTab : "single"],
  });

  const getSourceDragFormat = React.useCallback(
    (source: SourceData): "path" | "expression" | "config" => {
      if (source.isConfig === true) return "config";
      const obj = source.data;
      if (obj && typeof obj === "object" && !Array.isArray(obj)) {
        const configKeys = ["uid", "tabId", "configType", "isDecript"];
        if (configKeys.some((key) => key in obj)) return "config";
      }
      return dragDataFormat;
    },
    [dragDataFormat],
  );

  const buildDragExpression = (
    rawPath: string,
    format?: "path" | "expression" | "config",
  ): string => {
    const effectiveFormat = format || dragDataFormat;
    if (effectiveFormat === "expression") {
      return rawPath.startsWith("[")
        ? `{{$json${rawPath}}}`
        : `{{$json.${rawPath}}}`;
    }
    if (effectiveFormat === "config") {
      const flat = rawPath.replace(/\[\d+\]/g, "").replace(/^\.+/, "");
      return `{{config.${flat}}}`;
    }
    return rawPath;
  };

  const handleDragStartInternal = (
    e: React.DragEvent,
    rawPath: string,
    value: any,
    format?: "path" | "expression" | "config",
  ) => {
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "copy";
    const transparentDragImage = document.createElement("canvas");
    transparentDragImage.width = 1;
    transparentDragImage.height = 1;
    e.dataTransfer.setDragImage(transparentDragImage, 0, 0);
    document.body.style.cursor = "grabbing";
    const expression = buildDragExpression(rawPath, format);
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({ path: expression, value }),
    );
    onDragStart?.(rawPath, value);
  };

  const handleDragEndInternal = () => {
    document.body.style.cursor = "default";
  };

  const handleCopyExpressionInternal = async (
    e: React.MouseEvent,
    rawPath: string,
    format?: "path" | "expression" | "config",
  ) => {
    e.stopPropagation();
    const expression = buildDragExpression(rawPath, format);

    try {
      await navigator.clipboard.writeText(expression);
      toast({
        title: "Copied",
        description: expression,
        variant: "success",
        duration: 2000,
      });
    } catch {
      toast({
        title: "Copy failed",
        description: "Unable to copy expression to clipboard.",
        variant: "destructive",
        duration: 2000,
      });
    }
  };

  const renderColoredJSON = (
    obj: any,
    prefix: string = "",
    indent: number = 0,
    format?: "path" | "expression" | "config",
    searchActive: boolean = false,
  ): React.ReactNode => {
    const indentStr = "  ".repeat(indent);

    if (obj === null || obj === undefined) {
      return (
        <span className="text-muted-foreground italic">
          {obj === null ? "null" : "undefined"}
        </span>
      );
    }

    if (Array.isArray(obj)) {
      if (obj.length === 0) return <span>[]</span>;
      return (
        <>
          <span>[</span>
          {obj.map((item, index) => {
            const itemPath = prefix ? `${prefix}[${index}]` : "";
            return (
              <div key={index} className="pl-4">
                {renderColoredJSON(
                  item,
                  itemPath,
                  indent + 1,
                  format,
                  searchActive,
                )}
                {index < obj.length - 1 && <span>,</span>}
              </div>
            );
          })}
          <div>{indentStr}]</div>
        </>
      );
    }

    if (typeof obj === "object") {
      const keys = Object.keys(obj);
      if (keys.length === 0) return <span>{"{}"}</span>;
      const filteredKeys =
        prefix === "" ? keys.filter((k) => !excludeKeys.includes(k)) : keys;
      if (filteredKeys.length === 0) return <span>{"{}"}</span>;

      return (
        <>
          <span>&#123;</span>
          {filteredKeys.map((key, index) => {
            const fullPath = prefix ? `${prefix}.${key}` : key;
            const value = obj[key];
            const dragExpression = buildDragExpression(fullPath, format);
            const dragTooltip = `Drag to use or click to copy: ${dragExpression}`;

            const { isMatch, matchClassName, segments } =
              keySearch.getKeyMatchInfo(key, searchActive);

            const keyText = segments ? (
              <>
                {segments.before}
                <span className={matchClassName}>{segments.match}</span>
                {segments.after}
              </>
            ) : (
              key
            );

            const keyElement = draggable ? (
              <span
                draggable={true}
                onDragStart={(e) =>
                  handleDragStartInternal(e, fullPath, value, format)
                }
                onDragEnd={handleDragEndInternal}
                onClick={(e) =>
                  handleCopyExpressionInternal(e, fullPath, format)
                }
                data-search-match={isMatch ? "true" : undefined}
                className="nodrag cursor-grab active:cursor-grabbing hover:bg-lightGray hover:shadow-sm px-1.5 py-0.5 rounded font-semibold text-primary select-none transition-all border border-transparent hover:border-primary/40"
                title={dragTooltip}
              >
                &quot;{keyText}&quot;
              </span>
            ) : (
              <span
                data-search-match={isMatch ? "true" : undefined}
                className="font-semibold text-primary"
              >
                &quot;{keyText}&quot;
              </span>
            );

            return (
              <div key={key} className="pl-4">
                {keyElement}
                <span>: </span>
                {typeof value === "string" ? (
                  <span style={{ color: "var(--chart-2)" }}>
                    &quot;{value}&quot;
                  </span>
                ) : typeof value === "number" ? (
                  <span style={{ color: "var(--chart-4)" }}>{value}</span>
                ) : typeof value === "boolean" ? (
                  <span style={{ color: "var(--ndr-orange)" }}>
                    {String(value)}
                  </span>
                ) : (
                  renderColoredJSON(
                    value,
                    fullPath,
                    indent + 1,
                    format,
                    searchActive,
                  )
                )}
                {index < filteredKeys.length - 1 && <span>,</span>}
              </div>
            );
          })}
          <div>{indentStr}&#125;</div>
        </>
      );
    }

    if (typeof obj === "string") {
      return <span style={{ color: "var(--chart-2)" }}>&quot;{obj}&quot;</span>;
    }
    if (typeof obj === "number") {
      return <span style={{ color: "var(--chart-4)" }}>{obj}</span>;
    }
    if (typeof obj === "boolean") {
      return <span style={{ color: "var(--ndr-orange)" }}>{String(obj)}</span>;
    }
    return <span>{String(obj)}</span>;
  };

  if (!data || (typeof data === "object" && Object.keys(data).length === 0)) {
    if (!sources || sources.length === 0) {
      return (
        <div className={`text-xs text-muted-foreground italic ${className}`}>
          <FallBackUi title="No Data Available" />
        </div>
      );
    }
  }

  if (normalizedSources.length === 0) {
    return (
      <div className={`text-xs text-muted-foreground italic ${className}`}>
        <FallBackUi title="No Data Available" />
      </div>
    );
  }

  const searchBadge = (
    <KeySearchBadge
      searchQuery={keySearch.searchQuery}
      onSearchChange={keySearch.setSearchQuery}
      matchCount={keySearch.matchCount}
      currentMatchIndex={keySearch.currentMatchIndex}
      isExpanded={keySearch.isSearchExpanded}
      onMouseEnter={keySearch.onMouseEnter}
      onMouseLeave={keySearch.onMouseLeave}
      onIconClick={keySearch.handleSearchIconClick}
      onKeyDown={keySearch.handleSearchKeyDown}
      onFocus={keySearch.onFocus}
      onBlur={keySearch.onBlur}
      onPrev={keySearch.goToPrevMatch}
      onNext={keySearch.goToNextMatch}
      inputRef={keySearch.searchInputRef}
    />
  );

  // ── Single source ────────────────────────────────────────────────────────────
  // Mirrors ResponseViewer exactly:
  //   outer div          → flex flex-col h-full (fills NodeContentSection)
  //   optional header    → flex-shrink-0
  //   scroll div         → flex-1 min-h-0 overflow-y-auto  ← SINGLE scroll surface
  if (normalizedSources.length === 1 && !useTabs) {
    const source = normalizedSources[0];
    const sourceDragFormat = getSourceDragFormat(source);
    keySearch.resetMatchCounter();

    return (
      <div
        className={`flex flex-col h-full ${className}`}
        {...keySearch.containerMouseHandlers}
      >
        {/* Header — flex-shrink-0, stays visible, never scrolls away */}
        {showHeader && (
          <div className="flex items-center justify-between px-2 pb-2 border-b flex-shrink-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold">Input</h3>
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  className="text-muted-foreground hover:text-primary transition"
                  title="Refresh input data"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" />
                  </svg>
                </button>
              )}
            </div>
            {sourceCount > 0 && (
              <div className="text-xs text-muted-foreground italic">
                {sourceCount} source{sourceCount > 1 ? "s" : ""}
              </div>
            )}
          </div>
        )}
        {showLabel && label && (
          <div className="text-xs font-medium mb-1 text-muted-foreground flex-shrink-0">
            {label}
          </div>
        )}

        {/* Single scroll surface — identical to ResponseViewer's inner scroll div */}
        <div className="flex-1 min-h-0 relative">
          {searchBadge}
          <div
            className="h-full overflow-y-auto overflow-x-hidden"
            ref={(el) => {
              panelContainerRefs.current.single = el;
            }}
          >
            <div className="text-xs font-mono p-2 overflow-x-auto">
              {renderColoredJSON(source.data, "", 0, sourceDragFormat, true)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Multiple sources — tabs ──────────────────────────────────────────────────
  // Same pattern: outer flex col h-full, header flex-shrink-0,
  // tab bar flex-shrink-0, content area flex-1 min-h-0 overflow-y-auto
  return (
    <div
      className={`flex flex-col h-full ${className}`}
      {...keySearch.containerMouseHandlers}
    >
      {/* Heading — flex-shrink-0 */}
      <div className="flex items-center justify-between px-3 py-2 border-b flex-shrink-0 bg-muted/40">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">Input</h3>
          {normalizedSources.length > 0 && (
            <div className="text-sm text-muted-foreground italic">
              {normalizedSources.length} source
              {normalizedSources.length > 1 ? "s" : ""}
            </div>
          )}
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            className="text-muted-foreground hover:text-primary transition"
            title="Refresh input data"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 2v6h-6M3 12a9 9 0 0 1 15-6.7L21 8M3 22v-6h6M21 12a9 9 0 0 1-15 6.7L3 16" />
            </svg>
          </button>
        )}
      </div>

      {/* Tabs — flex-1 min-h-0 so content area can stretch */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex flex-col flex-1 min-h-0"
      >
        {/* Tab bar — flex-shrink-0 */}
        <div className="px-3 pt-2 pb-2 border-b flex-shrink-0 bg-muted/40">
          <TabsList className="h-8 bg-transparent p-0 gap-1">
            {normalizedSources.map((source, index) => {
              const dragFormat = getSourceDragFormat(source);
              const isConfigNode =
                source.isConfig === true || dragFormat === "config";
              const displayLabel = isConfigNode
                ? "Configuration"
                : source.label;
              return (
                <TabsTrigger
                  key={index}
                  value={String(index)}
                  className="text-xs h-7 px-3 data-[state=active]:bg-primary data-[state=active]:text-background data-[state=active]:shadow-sm bg-muted text-muted-foreground rounded-sm hover:bg-lightGray"
                >
                  {displayLabel}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {/* Tab content area — flex-1 min-h-0 overflow-hidden */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {normalizedSources.map((source, index) => {
            const sourceDragFormat = getSourceDragFormat(source);
            const isActivePanel = String(index) === activeTab;
            if (isActivePanel) {
              keySearch.resetMatchCounter();
            }
            return (
              <TabsContent
                key={index}
                value={String(index)}
                className="h-full m-0 p-0 data-[state=inactive]:hidden"
              >
                {/* Single scroll surface per tab */}
                <div className="h-full relative">
                  {isActivePanel && searchBadge}
                  <div
                    className="h-full overflow-y-auto overflow-x-hidden"
                    ref={(el) => {
                      panelContainerRefs.current[String(index)] = el;
                    }}
                  >
                    <div className="text-xs font-mono p-2 overflow-x-auto">
                      {renderColoredJSON(
                        source.data,
                        "",
                        0,
                        sourceDragFormat,
                        isActivePanel,
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </div>
      </Tabs>
    </div>
  );
};

const arePropsEqual = (
  prevProps: InputViewerProps,
  nextProps: InputViewerProps,
) => {
  if (
    prevProps.draggable !== nextProps.draggable ||
    prevProps.label !== nextProps.label ||
    prevProps.showLabel !== nextProps.showLabel ||
    prevProps.className !== nextProps.className ||
    prevProps.dragDataFormat !== nextProps.dragDataFormat ||
    prevProps.showHeader !== nextProps.showHeader ||
    prevProps.sourceCount !== nextProps.sourceCount ||
    prevProps.onDragStart !== nextProps.onDragStart ||
    prevProps.onRefresh !== nextProps.onRefresh
  ) {
    return false;
  }
  if (
    JSON.stringify(prevProps.excludeKeys) !==
    JSON.stringify(nextProps.excludeKeys)
  ) {
    return false;
  }
  if (JSON.stringify(prevProps.sources) !== JSON.stringify(nextProps.sources)) {
    return false;
  }
  const prevData = prevProps.data ? JSON.stringify(prevProps.data) : null;
  const nextData = nextProps.data ? JSON.stringify(nextProps.data) : null;
  return prevData === nextData;
};

export default React.memo(InputViewer, arePropsEqual);
